import { api } from '../api';
import { isApp } from '../native/env';

/*
 * Уведомления о сообщениях. Два разных механизма:
 *
 * 1. Приложение открыто — уведомление показывает service worker по просьбе
 *    страницы (registration.showNotification), чтобы его мог закрыть любой
 *    экземпляр приложения. Работает в любом браузере на любой системе.
 * 2. Приложение закрыто — веб-пуш через service worker. На компьютерах
 *    работает везде, на Android тоже, а на iPhone только если приложение
 *    добавлено на домашний экран: в обычной вкладке Safari пушей нет.
 *
 * Сервер шлёт пуш только тем, у кого нет живого потока событий, — поэтому
 * два уведомления об одном сообщении прийти не могут.
 */

export type PushState = {
  /** Браузер вообще умеет веб-пуши. */
  supported: boolean;
  /** Ключ VAPID с сервера получен: без него подписка невозможна. */
  configured: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
  /** iOS требует установки на домашний экран — иначе пушей не будет. */
  needsInstall: boolean;
};

const isIos = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPadOS 13+ притворяется макинтошем, отличаем по тач-экрану
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/** Приложение запущено с домашнего экрана, а не во вкладке браузера. */
export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as { standalone?: boolean }).standalone === true;

/** На айфоне пуши доступны только установленному приложению; приложение iPhone им и является. */
export const needsInstall = (): boolean => !isApp() && isIos() && !isStandalone();

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function notificationsSupported(): boolean {
  return 'Notification' in window;
}

function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

let swReady: Promise<ServiceWorkerRegistration> | null = null;

export function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!swReady) swReady = navigator.serviceWorker.register('/sw.js');
  return swReady;
}

const ENDPOINT_KEY = 'code-push-endpoint';

/**
 * Адрес пуш-подписки этого устройства, каким мы его запомнили в прошлый раз
 * (пусто, если подписки нет). Синхронный: поток событий открывается сразу,
 * не дожидаясь ответа системы — на iPhone после возврата из фона
 * `getSubscription()` отвечал по 15 секунд, и всё это время телефон был
 * без событий.
 */
export function cachedEndpoint(): string {
  try {
    return localStorage.getItem(ENDPOINT_KEY) ?? '';
  } catch {
    return '';
  }
}

/**
 * Адрес пуш-подписки этого устройства от самой системы (пусто, если её
 * нет). Передаётся в поток событий: так сервер знает, какое устройство на
 * связи, и не шлёт ему пуш, а остальные устройства человека будит.
 */
export async function currentEndpoint(): Promise<string> {
  if (!pushSupported()) return '';
  let endpoint = '';
  try {
    const reg = await registerServiceWorker();
    endpoint = (await reg.pushManager.getSubscription())?.endpoint ?? '';
  } catch {
    endpoint = '';
  }
  try {
    if (endpoint) localStorage.setItem(ENDPOINT_KEY, endpoint);
    else localStorage.removeItem(ENDPOINT_KEY);
  } catch {
    /* без памяти обойдёмся системным ответом */
  }
  return endpoint;
}

/** Подписка появилась или исчезла: поток событий должен переподключиться. */
export const PUSH_CHANGED = 'code-push-changed';

export async function pushState(): Promise<PushState> {
  const supported = pushSupported();
  const permission = notificationsSupported() ? Notification.permission : 'denied';
  const base: PushState = {
    supported,
    configured: false,
    permission,
    subscribed: false,
    needsInstall: needsInstall(),
  };
  if (!supported) return base;

  const { publicKey } = await api.pushKey().catch(() => ({ publicKey: null }));
  base.configured = !!publicKey;
  try {
    const reg = await registerServiceWorker();
    base.subscribed = !!(await reg.pushManager.getSubscription());
  } catch {
    /* регистрация могла не пройти — состояние просто останется «не подписан» */
  }
  return base;
}

/**
 * Спрашивает разрешение и подписывает устройство. Возвращает текст ошибки,
 * если что-то не вышло, — его показывает экран настроек уведомлений.
 */
export async function enablePush(): Promise<string | null> {
  if (!pushSupported()) return 'Этот браузер не умеет уведомления при закрытом приложении';
  if (needsInstall()) {
    return 'На айфоне уведомления работают только после установки на домашний экран';
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'Уведомления запрещены в настройках браузера';

  const { publicKey } = await api.pushKey();
  if (!publicKey) return 'На сервере не настроены ключи уведомлений';

  const reg = await registerServiceWorker();
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    return 'Браузер не выдал ключи подписки';
  }
  await api.pushSubscribe({
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
  });
  window.dispatchEvent(new Event(PUSH_CHANGED));
  return null;
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await registerServiceWorker();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await api.pushUnsubscribe(sub.endpoint).catch(() => {});
  await sub.unsubscribe().catch(() => {});
  window.dispatchEvent(new Event(PUSH_CHANGED));
}

/**
 * Уведомление на рабочем столе, пока приложение открыто. Не показываем,
 * если окно в фокусе: человек и так видит сообщение.
 */
export function notifyLocally(title: string, body: string, channelId: string): void {
  if (!notificationsSupported() || Notification.permission !== 'granted') return;
  if (document.hasFocus()) return;
  const tag = `chat-${channelId}`;
  /*
   * Через service worker, а не конструктором: такое уведомление может закрыть
   * любая вкладка, когда чат прочитан, а клик открывает сам чат (sw.js).
   * Service worker нет или он ещё не включился — по-старому, конструктором:
   * лучше уведомление, которое само не закроется, чем никакого.
   */
  const direct = () => {
    try {
      const n = new Notification(title, { body, tag, icon: '/icon.svg' });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      /* некоторые браузеры запрещают конструктор вне service worker */
    }
  };
  const sw = navigator.serviceWorker;
  if (!sw) return direct();
  void sw
    .getRegistration()
    .then(
      (reg) =>
        reg?.active
          ? reg.showNotification(title, { body, tag, icon: '/icon.svg', data: { channelId, messageId: 0 } })
          : direct(),
      direct,
    )
    .catch(() => {});
}

/**
 * Закрыть показанные системные уведомления по метке — те, что показала любая
 * вкладка, и те, что пришли пушем. Так уведомление о звонке и о прочитанном
 * чате пропадает, как только оно больше не нужно (спека
 * 2026-10-02-tabs-devices-sync).
 */
export async function closeShown(match: (tag: string) => boolean): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const shown = (await reg?.getNotifications()) ?? [];
    for (const n of shown) if (match(n.tag)) n.close();
  } catch {
    /* уведомления не поддерживаются — закрывать нечего */
  }
}
